import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import { useState, useEffect } from "react";
import tagHierarchy from "../data/tag-hierarchy.json";

interface TagHierarchyItem {
  name: string;
  tags: string[];
}

interface TagHierarchySubcategory {
  name: string;
  items: TagHierarchyItem[];
}

interface TagHierarchyCategory {
  name: string;
  subcategories: TagHierarchySubcategory[];
}

interface TagHierarchy {
  categories: TagHierarchyCategory[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (selectedTags: string[]) => void;
  currentTags?: string[];
}

export const TagSelectorModal = (props: Props) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const hierarchy: TagHierarchy = tagHierarchy;

  // Initialize selected items based on current tags
  useEffect(() => {
    if (props.currentTags && props.open) {
      const newSelectedItems = new Set<string>();
      
      // Find which items should be selected based on current tags
      hierarchy.categories.forEach(category => {
        category.subcategories.forEach(subcategory => {
          subcategory.items.forEach(item => {
            const itemTags = item.tags;
            // Check if all tags for this item are present in current tags
            const allTagsPresent = itemTags.every(tag => 
              props.currentTags?.includes(tag)
            );
            if (allTagsPresent) {
              newSelectedItems.add(item.name);
            }
          });
        });
      });
      
      setSelectedItems(newSelectedItems);
    }
  }, [props.currentTags, props.open, hierarchy]);

  // Auto-expand nodes that have selected items
  useEffect(() => {
    const newExpandedNodes = new Set<string>();
    
    hierarchy.categories.forEach(category => {
      const hasSelectedItems = category.subcategories.some(subcategory =>
        subcategory.items.some(item => selectedItems.has(item.name))
      );
      if (hasSelectedItems) {
        newExpandedNodes.add(category.name);
        // Also expand subcategories if they have selected items
        category.subcategories.forEach(subcategory => {
          if (subcategory.items.some(item => selectedItems.has(item.name))) {
            newExpandedNodes.add(`${category.name}-${subcategory.name}`);
          }
        });
      }
    });
    
    setExpandedNodes(newExpandedNodes);
  }, [selectedItems, hierarchy]);

  const handleItemToggle = (itemName: string) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemName)) {
      newSelectedItems.delete(itemName);
    } else {
      newSelectedItems.add(itemName);
    }
    setSelectedItems(newSelectedItems);
  };

  const handleNodeToggle = (nodeKey: string) => {
    const newExpandedNodes = new Set(expandedNodes);
    if (newExpandedNodes.has(nodeKey)) {
      newExpandedNodes.delete(nodeKey);
    } else {
      newExpandedNodes.add(nodeKey);
    }
    setExpandedNodes(newExpandedNodes);
  };

  const handleSave = () => {
    // Collect all tags from selected items
    const allSelectedTags: string[] = [];
    
    hierarchy.categories.forEach(category => {
      category.subcategories.forEach(subcategory => {
        subcategory.items.forEach(item => {
          if (selectedItems.has(item.name)) {
            allSelectedTags.push(...item.tags);
          }
        });
      });
    });

    // Remove duplicates and sort
    const uniqueTags = [...new Set(allSelectedTags)].sort();
    props.onSave(uniqueTags);
  };

  const handleCancel = () => {
    setSelectedItems(new Set());
    props.onClose();
  };

  const hasSelections = selectedItems.size > 0;

  const renderTreeNode = (
    label: string,
    nodeKey: string,
    children: React.ReactNode,
    level: number = 0
  ) => {
    const isExpanded = expandedNodes.has(nodeKey);
    const hasChildren = children !== null;

    return (
      <Box key={nodeKey}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: level * 2,
            mb: 0.5,
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={() => handleNodeToggle(nodeKey)}
              sx={{ mr: 0.5, p: 0.25 }}
            >
              {isExpanded ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
          )}
          {!hasChildren && <Box sx={{ width: 32 }} />}
          <Typography
            variant={level === 0 ? "subtitle1" : "body2"}
            sx={{
              fontWeight: level === 0 ? 600 : level === 1 ? 500 : 400,
              color: level === 0 ? 'text.primary' : level === 1 ? 'text.secondary' : 'text.primary',
            }}
          >
            {label}
          </Typography>
        </Box>
        {hasChildren && (
          <Collapse in={isExpanded}>
            <Box>{children}</Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Select Product Tags</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Select the categories that apply to this product. The tags will be used to display the product in the appropriate sections of the website navigation.
        </Typography>
        
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', userSelect: 'none' }}>
          {hierarchy.categories.map((category) => {
            const categoryKey = category.name;
            const categoryChildren = category.subcategories.map((subcategory) => {
              const subcategoryKey = `${category.name}-${subcategory.name}`;
              
              // For single subcategory, show items directly under category
              if (category.subcategories.length === 1) {
                return subcategory.items.map((item) => (
                  <Box key={item.name} sx={{ ml: 2, mb: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedItems.has(item.name)}
                          onChange={() => handleItemToggle(item.name)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            {item.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            ({item.tags.join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ));
              }

              // For multiple subcategories, show subcategory as expandable node
              const subcategoryChildren = subcategory.items.map((item) => (
                <Box key={item.name} sx={{ ml: 4, mb: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedItems.has(item.name)}
                        onChange={() => handleItemToggle(item.name)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {item.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          ({item.tags.join(', ')})
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ));

              return renderTreeNode(
                subcategory.name,
                subcategoryKey,
                subcategoryChildren,
                1
              );
            });

            return renderTreeNode(
              category.name,
              categoryKey,
              categoryChildren,
              0
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave}
          disabled={!hasSelections}
        >
          Save Tags
        </Button>
      </DialogActions>
    </Dialog>
  );
};